import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import {
  GET_ROLES,
  CREATE_ROLE,
  UPDATE_ROLE,
  DELETE_ROLE,
} from '../../api/role'
import { GET_WORKFLOWS } from '../../api/workflow'
import { toast } from 'react-toastify'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Tab } from 'react-bootstrap'

interface Role {
  id: number
  name: string
  scope: string
  description?: string
  workflow?: {
    id: number
    name: string
  } | null
}

interface Workflow {
  id: number
  name: string
}

const Roles: React.FC = () => {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingRole, setEditingRole] = useState<Partial<Role>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const {
    data: rolesData,
    loading: rolesLoading,
    refetch,
  } = useQuery(GET_ROLES)
  const { data: workflowsData, loading: workflowsLoading } =
    useQuery(GET_WORKFLOWS)

  const [createRole] = useMutation(CREATE_ROLE, {
    onCompleted: () => refetch(),
  })
  const [updateRole] = useMutation(UPDATE_ROLE, {
    onCompleted: () => refetch(),
  })
  const [deleteRole] = useMutation(DELETE_ROLE, {
    onCompleted: () => refetch(),
  })

  const roles: Role[] = rolesData?.userRoles || []
  const workflows: Workflow[] = workflowsData?.workflows || []

  const handleEdit = (role: Role) => {
    setEditingId(role.id)
    setEditingRole({
      name: role.name,
      scope: role.scope,
      description: role.description,
      workflow: role.workflow,
    })
  }

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateRole({
          variables: {
            id: editingId,
            input: {
              name: editingRole.name,
              scope: editingRole.scope,
              description: editingRole.description,
              workflow: editingRole.workflow
                ? { connect: { id: editingRole.workflow.id } }
                : undefined,
            },
          },
        })
        toast.success(`Role updated successfully`)
        setSuccessMessage('Role updated successfully')
      } else {
        await createRole({
          variables: {
            input: {
              name: editingRole.name,
              scope: editingRole.scope,
              description: editingRole.description,
              workflow: editingRole.workflow
                ? { connect: { id: editingRole.workflow.id } }
                : undefined,
            },
          },
        })
        setSuccessMessage('Role created successfully')
      }
      setShowSuccess(true)
      setEditingId(null)
      setEditingRole({})
      setIsAdding(false)
    } catch (err: any) {
      toast.error(err.message || 'Failed to save role')
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return
    try {
      await deleteRole({ variables: { id } })
      setSuccessMessage('Role deleted successfully')
      setShowSuccess(true)
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete role')
    }
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditingRole({})
    setIsAdding(false)
  }

  if (rolesLoading || workflowsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Roles Management
      </Typography>

      <Box mb={2}>
        <IconButton
          title="Add Role"
          color="primary"
          onClick={() => {
            setIsAdding(true)
            setEditingId(null)
            setEditingRole({ name: '', scope: 'BRANCH', description: '' })
          }}
        >
          <AddCircleIcon fontSize="medium" color="success" />
        </IconButton>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Workflow</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isAdding && (
              <TableRow>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Name"
                    value={editingRole.name || ''}
                    onChange={(e) =>
                      setEditingRole({ ...editingRole, name: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControl>
                    <Select
                      value={editingRole.scope || 'BRANCH'}
                      onChange={(e) =>
                        setEditingRole({
                          ...editingRole,
                          scope: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="BRANCH">Branch</MenuItem>
                      <MenuItem value="NATIONAL">National</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Description"
                    value={editingRole.description || ''}
                    onChange={(e) =>
                      setEditingRole({
                        ...editingRole,
                        description: e.target.value,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={editingRole.workflow?.id || ''}
                      onChange={(e) => {
                        const wf = workflows.find(
                          (w) => w.id === Number(e.target.value)
                        )
                        setEditingRole({
                          ...editingRole,
                          workflow: wf
                            ? { id: wf.id, name: wf.name }
                            : undefined,
                        })
                      }}
                    >
                      <MenuItem value="">No Workflow</MenuItem>
                      {workflows.map((wf) => (
                        <MenuItem key={wf.id} value={wf.id}>
                          {wf.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={handleSave} color="primary">
                    <SaveIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancel}
                    color="secondary"
                  >
                    <CancelIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell>
                  {editingId === role.id ? (
                    <TextField
                      size="small"
                      value={editingRole.name || ''}
                      onChange={(e) =>
                        setEditingRole({ ...editingRole, name: e.target.value })
                      }
                    />
                  ) : (
                    role.name
                  )}
                </TableCell>
                <TableCell>
                  {editingId === role.id ? (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={editingRole.scope || ''}
                        onChange={(e) =>
                          setEditingRole({
                            ...editingRole,
                            scope: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="BRANCH">Branch</MenuItem>
                        <MenuItem value="NATIONAL">National</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    role.scope
                  )}
                </TableCell>
                <TableCell>
                  {editingId === role.id ? (
                    <TextField
                      size="small"
                      value={editingRole.description || ''}
                      onChange={(e) =>
                        setEditingRole({
                          ...editingRole,
                          description: e.target.value,
                        })
                      }
                    />
                  ) : (
                    role.description || '-'
                  )}
                </TableCell>
                <TableCell>
                  {editingId === role.id ? (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={editingRole.workflow?.id || ''}
                        onChange={(e) => {
                          const wf = workflows.find(
                            (w) => w.id === Number(e.target.value)
                          )
                          setEditingRole({
                            ...editingRole,
                            workflow: wf
                              ? { id: wf.id, name: wf.name }
                              : undefined,
                          })
                        }}
                      >
                        <MenuItem value="">No Workflow</MenuItem>
                        {workflows.map((wf) => (
                          <MenuItem key={wf.id} value={wf.id}>
                            {wf.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    role.workflow?.name || '-'
                  )}
                </TableCell>
                <TableCell>
                  {editingId === role.id ? (
                    <>
                      <IconButton
                        size="small"
                        onClick={handleSave}
                        color="primary"
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancel}
                        color="secondary"
                      >
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton size="small" onClick={() => handleEdit(role)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(role.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Roles
